/**
 * Format a Unix epoch (seconds) into a date string parseable by Symfony
 * `DateConvertManager`. Used exclusively by the maintenance API endpoints
 * (`addMaintenance` / `updateMaintenance` / `moveMaintenance`).
 *
 * Why this exists (Bug R, HW-942 Rem16):
 *   - Regular event endpoints route through `EventParamsRequestDto::getStartTimestamp`,
 *     which has an `isTimestamp()` short-circuit: numeric input is cast to int
 *     without invoking `DateConvertManager`.
 *   - `MaintenanceParamsRequestDto::getStartTime/getFinishTime` has NO such
 *     short-circuit — it always calls `$dateConvertManager->getDate($value)`.
 *   - `DateConvertManager::getDateForWeb` parses `INCOME_DATE_FORMAT='D M d Y H:i:s e'`
 *     and a small set of fallbacks. A stringified epoch like `"1778628600"` matches
 *     none → backend throws `RuntimeException("Failed to save. Incorrect date format", 400)`.
 *
 * Why we can't just use `Date.toString()`: browser `toString()` formats in the
 * BROWSER timezone. The backend then re-parses and re-converts using the
 * resource location timezone (`MaintenanceManager.php:759-769`). If browser tz
 * differs from resource tz, the wall-clock silently shifts.
 *
 * Output shape mirrors the browser `Date.toString()` format that backend
 * confirms it parses (via the `INCOME_DATE_FORMAT` + `getSpaceCount` wildcard
 * logic), but rendered in the SUPPLIED location tz:
 *
 *   "Mon Jan 05 2026 12:00:00 GMT+0300 (Eastern European Summer Time)"
 *
 * Long-term: backender may add an `isTimestamp` short-circuit to the
 * maintenance DTO (Backend ask R' in parent epic). When that lands this helper
 * becomes redundant.
 *
 * @param {number|string} epoch  Unix epoch in seconds.
 * @param {string} [tz]  IANA timezone (e.g. "America/New_York"). Falls back to
 *   the browser tz when missing — callers should always supply.
 * @returns {string}  Empty string when epoch is not a finite number.
 */
export function formatMaintenanceDate(epoch, tz) {
	// Reject nullish + non-numeric input fast. `Number(null) === 0` would
	// otherwise silently emit the 1970-01-01 epoch, which backend rejects
	// later with a less obvious error message.
	if (epoch === null || epoch === undefined || epoch === '') return '';
	const epochSeconds = typeof epoch === 'number' ? epoch : Number(epoch);
	if (!Number.isFinite(epochSeconds)) return '';
	const date = new Date(epochSeconds * 1000);
	const zone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

	// `longOffset` emits "GMT+03:00"; `Date.toString()` shape is "GMT+0300"
	// (no colon). Stripping it keeps the wildcard-fallback parser happy.
	const offsetParts = new Intl.DateTimeFormat('en-US', {
		timeZone: zone,
		timeZoneName: 'longOffset'
	}).formatToParts(date);
	const offsetRaw = offsetParts.find(p => p.type === 'timeZoneName')?.value || 'GMT+00:00';
	// `longOffset` collapses UTC to bare "GMT"; spell it out so the PHP `e`
	// token + wildcard fallback gets a consistent "GMT±HHMM" shape across tzs.
	const offset = (offsetRaw === 'GMT' ? 'GMT+0000' : offsetRaw.replace(':', ''));

	// `long` gives "Eastern European Summer Time"; falls back to the Olson id
	// when the runtime can't resolve a localized long name.
	const longParts = new Intl.DateTimeFormat('en-US', {
		timeZone: zone,
		timeZoneName: 'long'
	}).formatToParts(date);
	const longTz = longParts.find(p => p.type === 'timeZoneName')?.value || zone;

	// Wall-clock parts rendered in the target tz. `en-US` locale guarantees
	// the short weekday/month tokens backend's `D` / `M` PHP formats expect.
	const walls = new Intl.DateTimeFormat('en-US', {
		timeZone: zone,
		weekday: 'short',
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false
	}).formatToParts(date).reduce((acc, p) => {
		if (p.type !== 'literal') acc[p.type] = p.value;
		return acc;
	}, {});

	// Some runtimes emit "24" for midnight under hour12:false; PHP `H` expects
	// "00". Normalize.
	const hour = walls.hour === '24' ? '00' : walls.hour;

	return `${walls.weekday} ${walls.month} ${walls.day} ${walls.year} ${hour}:${walls.minute}:${walls.second} ${offset} (${longTz})`;
}
